import { Box, Stack, Tooltip as ChakraTooltip } from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import { Button, SearchField, Switch, Text, Tooltip } from '@main/complykit';
import { useGetOrganizationQuery } from '@main/graphql/queries/GetOrganization.generated';
import { toError } from '@main/shared/utils';
import { errorToast, successToast } from '@main/ui';
import { useUserData } from '@nhost/react';
import { useMemo, useState } from 'react';

import {
  GetOrganizationAgentsQuery,
  useGetOrganizationAgentsQuery,
  useSetOrganizationAgentsEnabledMutation,
} from './agents-tab.admin.generated';

type CatalogAgent = GetOrganizationAgentsQuery['grove_agents'][number];

function moduleOf(agent: CatalogAgent): string | null {
  const manifest = agent.manifest as { module?: unknown } | null;
  return manifest && typeof manifest.module === 'string' ? manifest.module : null;
}

function enabledByLabel(instance: CatalogAgent['instances'][number] | undefined): string | null {
  if (!instance?.enabled) return null;
  const who = instance.enabled_by_user?.displayName ?? instance.enabled_by_user?.email;
  const when = instance.enabled_at ? new Date(instance.enabled_at).toLocaleDateString() : null;
  if (who && when) return `${who}, ${when}`;
  return when ?? who ?? null;
}

export const AgentsTab = ({ orgId }: { orgId: string }) => {
  const { data: organizationData } = useGetOrganizationQuery({ orgId });
  const isGroveEnabled = organizationData?.organizations_by_pk?.is_grove_enabled ?? false;
  const { data, refetch } = useGetOrganizationAgentsQuery({ orgId });
  const [setEnabled] = useSetOrganizationAgentsEnabledMutation();
  const [savingIds, setSavingIds] = useState<ReadonlySet<string>>(() => new Set());
  const user = useUserData();
  const [search, setSearch] = useState('');

  const agents = useMemo(() => data?.grove_agents ?? [], [data]);
  const visibleAgents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter((agent) =>
      [agent.name, agent.description ?? '', moduleOf(agent) ?? ''].some((s) =>
        s.toLowerCase().includes(q),
      ),
    );
  }, [agents, search]);
  const enabledCount = agents.filter((agent) => agent.instances[0]?.enabled).length;
  const allEnabled = agents.length > 0 && enabledCount === agents.length;

  const apply = async (targets: CatalogAgent[], enabled: boolean) => {
    const ids = targets.map((agent) => agent.id);
    setSavingIds((current) => new Set([...current, ...ids]));
    try {
      await setEnabled({
        objects: targets.map((agent) => ({
          agent_id: agent.id,
          organization_id: orgId,
          enabled,
          enabled_at: enabled ? new Date().toISOString() : null,
          enabled_by: enabled ? (user?.id ?? null) : null,
        })),
      }).unwrap();
      await refetch().unwrap();
      successToast(enabled ? 'Agent enabled' : 'Agent disabled');
    } catch (error) {
      errorToast('Agent update failed');
      datadogLogs.logger.error('Agent availability update failed', {}, toError(error));
    } finally {
      setSavingIds((current) => {
        const next = new Set(current);
        ids.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  const enableAll = () => apply(agents, true);

  return (
    <Stack gap={4}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={3}>
        <Box w="full" maxW="xs">
          <SearchField
            aria-label="Search agents"
            placeholder="Search agents"
            clearLabel="Clear search"
            value={search}
            onChange={setSearch}
          />
        </Box>
        <Stack direction="row" alignItems="center" gap={3}>
          <Text size="xs" color="muted">
            {enabledCount} of {agents.length} enabled
          </Text>
          <Tooltip content="Enable Grove for this organization first" isDisabled={isGroveEnabled}>
            <Button
              isDisabled={!isGroveEnabled || allEnabled || savingIds.size > 0}
              onPress={() => void enableAll()}
            >
              Enable all
            </Button>
          </Tooltip>
        </Stack>
      </Stack>

      <Stack
        gap={0}
        borderWidth="1px"
        borderRadius="md"
        divider={<Stack borderBottomWidth="1px" />}
      >
        {visibleAgents.map((agent) => {
          const instance = agent.instances[0];
          const enabledBy = enabledByLabel(instance);
          return (
            <Stack
              key={agent.id}
              direction="row"
              alignItems="center"
              gap={3}
              px={3}
              minH="44px"
              title={agent.description ?? undefined}
            >
              <Stack direction="row" alignItems="center" flex="1 1 0" minW={0}>
                <Text size="sm" weight="medium">
                  {agent.name}
                </Text>
              </Stack>
              {enabledBy ? (
                <Text size="xs" color="muted">
                  {enabledBy}
                </Text>
              ) : null}
              {/* Parked with the Field round: a natively disabled Switch fires no
                  hover, and ComplyKit's disabled-trigger mechanism covers Button
                  and IconButton only. */}
              <ChakraTooltip
                label={!isGroveEnabled ? 'Enable Grove for this organization first' : ''}
              >
                <Stack>
                  <Switch
                    id={`agent-${agent.unique_id}`}
                    aria-label={`Enable ${agent.name}`}
                    isSelected={Boolean(instance?.enabled)}
                    isDisabled={!isGroveEnabled}
                    isPending={savingIds.has(agent.id)}
                    onChange={() => apply([agent], !instance?.enabled)}
                  />
                </Stack>
              </ChakraTooltip>
            </Stack>
          );
        })}
        {visibleAgents.length === 0 ? (
          <Text size="sm" color="muted">
            No agents match your search.
          </Text>
        ) : null}
      </Stack>
    </Stack>
  );
};
